import React, {useCallback, useMemo} from 'react';
import {useIntl} from 'react-intl';

import {useConfiguration, useCustomerMgr} from 'coreHooks';
import PaymentInstrumentModel from 'coreOrderModels/PaymentInstrumentModel';

import Button from 'brandComponents/Button';
import PaymentCardInfo from 'brandComponents/PaymentCardInfo';
import {useBasket} from 'brandHooks';

import {IPaymentInstrumentContent} from './IPaymentInstrumentContent';
import messages from './messages';

import styles from './PaymentInstrumentContent.module.scss';

const PaymentInstrumentContent = ({setStep, returnOrder, returnOrderData}: IPaymentInstrumentContent) => {
	const intl = useIntl();
	const isStoreCreditEnabled = useConfiguration('sitePreferences.StoreCreditEnable');
	const CustomerMgr = useCustomerMgr();
	const customer = CustomerMgr.getCurrentCustomer();
	const {applicablePaymentMethods} = useBasket();
	const specialRefundMethods = ['GIFT_CERTIFICATE', 'STORE_CREDIT'];
	const refundMethod = returnOrderData.getRefundMethod();

	const getPaymentCardName = useCallback((paymentMethodId: string): string => {
		const paymentMethod = applicablePaymentMethods?.find(
			(applicablePaymentMethod) => applicablePaymentMethod.id === paymentMethodId,
		);

		return paymentMethod ? paymentMethod?.name : paymentMethodId?.replace(/_/g, ' ');
	}, [applicablePaymentMethods]);

	const isEditable = useMemo(() => {
		return (isStoreCreditEnabled && customer?.isRegistered) || returnOrder.getPaymentInstruments().length > 1;
	}, [customer?.isRegistered]);

	const renderRefundMethod = () => {
		switch (refundMethod) {
			case 'GIFT_CERTIFICATE':
				return (
					<div className={styles['return-create-step-2__gift-certificate']}>
						{intl.formatMessage(messages.giftCertificateLabel)}
					</div>
				);
			case 'STORE_CREDIT':
				return <div className={styles['return-create-step-2__store-credit']}>{intl.formatMessage(messages.storeCreditLabel)}</div>;
		}
	};

	const renderPaymentInstrumentData = () => {
		let paymentInstrument: PaymentInstrumentModel | undefined;

		if (returnOrder?.getOriginalPaymentInstruments()?.[0]) {
			paymentInstrument = returnOrder.getOriginalPaymentInstruments()[0];
		} else if (returnOrder.getPaymentInstruments()?.[0]) {
			paymentInstrument = returnOrder.getPaymentInstruments()[0];
		}

		switch (paymentInstrument?.getPaymentMethodId()) {
			case 'CREDIT_CARD':
				return (
					<PaymentCardInfo
						paymentCard={paymentInstrument.getPaymentCard()}
						paymentInstrumentId={paymentInstrument.getPaymentInstrumentId()}
						allowDelete={false}
						classNames={styles['return-create-step-2__payment-card-info']}
					/>
				);
			default:
				if (!paymentInstrument) {
					return null;
				}

				return <span className="d-block">{getPaymentCardName(paymentInstrument.getPaymentMethodId())}</span>;
		}
	};

	const handleClick = () => setStep(1);

	return (
		<div className={styles['return-create-step-2__payment']}>
			<div className={styles['return-create-step-2__payment-title-wrapper']}>
				<span className={styles['return-create-step-2__products-title']}>{intl.formatMessage(messages.refundMethod)}</span>
				{isEditable && (
					<Button variant="tertiary" classNames={styles['modify-btn']} onClick={handleClick}>
						{intl.formatMessage(messages.modifyButtonText)}
					</Button>
				)}
			</div>
			{specialRefundMethods.includes(refundMethod) ? renderRefundMethod() : renderPaymentInstrumentData()}
		</div>
	);
};

export default PaymentInstrumentContent;
